import EventEmitter from "events";
import TypedEventEmitter from "typed-emitter";
import { TokenData } from "~/modules/auth";
import { Session, SessionEvent, SessionEventsType } from "~/modules/session";
import { InternalError } from "~/modules/error";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { getCurrentDate } from "~/utils/getCurrentDate";
import { accountConfigProvider } from "~/modules/config/AccountConfigDataGetter/getAccountConfigData";
import { AccountConfigData } from "~/backend/generated/Configuration/model/accountConfigData";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { TokenRegistry } from "~/backend/TokenRegistry";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { AccountConfigOptions } from "~/modules/config/AccountConfigOptions/AccountConfigOptions";
import { validateToken as validateTokenModule } from "~/modules/auth/ValidateToken/ValidateToken";
import { refreshToken as refreshTokenModule } from "~/modules/auth/RefreshToken/refreshToken";
import type { RefreshTokenParams, RefreshTokenResponse } from "~/modules/auth/RefreshToken";
import { ClientOptions } from "~/modules/client";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";

const TOKEN_ABOUT_TO_EXPIRE_MINUTES = 1;
const VERIFICATION_INTERVAL_MS = 15000;

export class SessionImpl
    extends (EventEmitter as unknown as new () => TypedEventEmitter<SessionEventsType>)
    implements Session
{
    #tokenExpiration: Date;

    readonly #accountConfigProvider: (
        sessionToken: string,
        options?: AccountConfigOptions
    ) => Promise<AccountConfigData>;

    #accountConfigData: AccountConfigData;

    #roles: Array<string> = [];

    #isActive: boolean = true;

    readonly #logger: Logger;

    #needsToAutoUpdateToken: boolean;

    #verificationInterval: ReturnType<typeof setInterval>;

    readonly #tokenStorer: TokenRegistry;

    readonly #clientOptions: ClientOptions;

    readonly #accountDataContainer: AccountConfigDataContainer;

    readonly #flexSessionIdentifier: string;

    readonly #validateTokenModule: (accountSid: string, token: string) => Promise<TokenData>;

    readonly #refreshTokenModule: (options: RefreshTokenParams) => Promise<RefreshTokenResponse>;

    constructor(ctx: ContextManager) {
        super();
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#accountConfigProvider = accountConfigProvider(ctx);
        this.#logger = getLogger(ctx)(LoggerName.Session);
        this.#logger.debug("Session constructed");
        this.#tokenStorer = ctx.retrieveInstanceOf(ContextInstance.TokenRegistry, TokenRegistry);
        this.#accountDataContainer = ctx.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );
        this.#flexSessionIdentifier = this.#generateUUID();
        this.#validateTokenModule = validateTokenModule(ctx);
        this.#refreshTokenModule = refreshTokenModule(ctx);
    }

    async init(token: string): Promise<void> {
        this.#logger.debug("will initialize session with token: ", token);
        this.#logger.debug("will update token: ", this.#clientOptions.session.autoUpdateToken);

        this.#tokenStorer.sessionToken = token;

        if (this.#clientOptions.session.autoUpdateToken && this.#clientOptions.session.refreshToken) {
            this.#tokenStorer.sessionRefreshToken = this.#clientOptions.session.refreshToken;
        }

        
        this.#accountConfigData = await this.#accountConfigProvider(token);
        this.#accountDataContainer.set(this.#accountConfigData);

        const accountSid = this.#accountConfigData.accountSid;
        const tokenData = await this.#validateTokenModule(accountSid, this.#tokenStorer.sessionToken);
        this.#roles = tokenData.roles;
        this.#tokenExpiration = new Date(tokenData.dateExpired);

        
        if (this.#clientOptions.session.autoUpdateToken) {
            this.#needsToAutoUpdateToken = this.#needsToAutoUpdateToken || this.#isTokenCloseToExpiry();
            this.#verificationInterval = setInterval(() => {
                void this.#verifyToken();
            }, VERIFICATION_INTERVAL_MS);
        }

        if (this.#needsToAutoUpdateToken) {
            await this.#autoUpdateToken();
        }

        return Promise.resolve();
    }

    updateToken(token: string, refreshToken?: string): void {
        this.#tokenStorer.sessionToken = token;
        this.#tokenStorer.sessionRefreshToken = refreshToken;
        this.emit(SessionEvent.TokenUpdated, token, refreshToken);

        this.#logger.debug("new token set");
    }

    readonly #verifyToken = async () => {
        if (this.#isTokenCloseToExpiry()) {
            await this.#autoUpdateToken();
        }
    };

    readonly #isTokenCloseToExpiry = () => {
        
        const now = getCurrentDate();
        const tokenTtlMinutes = (this.#tokenExpiration.getTime() - now.getTime()) / (60 * 1000);

        return tokenTtlMinutes <= TOKEN_ABOUT_TO_EXPIRE_MINUTES;
    };

    readonly #autoUpdateToken = async () => {
        this.#logger.debug("Auto-updating token");
        const { session } = this.#clientOptions;

        if (!session.autoUpdateToken) {
            this.#logger.warn("Auto-update token is disabled, skipping auto-update token");
            return;
        }
        const accountSid = this.#accountConfigData.accountSid;
        if (!accountSid) {
            throw new InternalError("Account sid not set");
        }

        let newToken: string | undefined;
        let newRefreshToken: string | undefined;
        let newTokenDateExpired: Date | undefined;
        try {
            const tokenRefreshResult = await this.#refreshTokenModule({
                refreshToken: this.#tokenStorer.sessionRefreshToken,
                appType: session.appType,
                isConsoleLogin: session.isConsoleLogin,
                accountSid: session.isConsoleLogin ? session.accountSid : undefined,
                instanceSid: session.isConsoleLogin ? session.instanceSid : undefined,
                ssoProfileSid: !session.isConsoleLogin ? session.ssoProfileSid : undefined
            });

            const tokenData = await this.#validateTokenModule(accountSid, tokenRefreshResult.accessToken);
            newTokenDateExpired = new Date(tokenData.dateExpired);
            newRefreshToken = tokenRefreshResult.refreshToken;

            if (newTokenDateExpired.getTime() === this.#tokenExpiration.getTime()) {
                this.#logger.warn("Token expiration not extended, because max lifetime reached");
                this.emit(SessionEvent.TokenMaxLifetimeReached, newTokenDateExpired);
            }

            newToken = tokenRefreshResult.accessToken;
        } catch (e) {
            this.#logger.error("Failed to refresh token", e);
            this.emit(SessionEvent.TokenAutoUpdateFailed);
        }

        if (!this.#isActive) {
            this.#logger.trace("AutoUpdateToken, session destroyed after refreshToken");
            return;
        }

        if (newToken && newRefreshToken && newTokenDateExpired) {
            try {
                this.updateToken(newToken, newRefreshToken);
                this.#tokenExpiration = newTokenDateExpired;
                this.#logger.info("Token auto-updated");
            } catch (e) {
                this.#logger.error("Failed to auto-update token", e);
                this.emit(SessionEvent.TokenAutoUpdateFailed);
            }
        }
    };

    #generateUUID(): string {
        const arr = new Uint8Array(16);
        crypto.getRandomValues(arr);
        return Array.from(arr)
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join("");
    }

    destroy(): void {
        if (this.#verificationInterval) {
            clearInterval(this.#verificationInterval);
        }
        this.#isActive = false;
    }

    get token(): string {
        return this.#tokenStorer.sessionToken;
    }

    get refreshToken(): string | undefined {
        return this.#tokenStorer.sessionRefreshToken;
    }

    get roles(): Array<string> {
        return this.#roles;
    }

    get flexSessionIdentifier(): string {
        return this.#flexSessionIdentifier;
    }

    get accountSid(): string {
        return this.#accountDataContainer.get().accountSid;
    }
}
